/*
 * MAKING A PICTURE SMALL ENOUGH TO LIVE IN A DOCUMENT.
 *
 * A dashboard background is stored INSIDE the board — see `DashboardBackground` in the proto for
 * why — which is what makes it durable, portable and impossible to lose. The price of that is size:
 * the board body is capped at 4 MiB and carries the widget source for every tile, and everybody who
 * opens the board downloads the whole thing.
 *
 * An image model hands back a PNG at full resolution, which is routinely one to two megabytes. So
 * the client re-encodes before the bytes ever reach the wire. WebP at ~1920 wide lands around
 * 150-400 KB — roughly a fifth of the original, for a picture nobody is going to inspect at 1:1
 * behind a row of gauges.
 *
 * DONE HERE RATHER THAN AT THE GATEWAY, deliberately. The gateway would have to decode and
 * re-encode an image in Go on the edge box that is also running the plant; the browser has a canvas
 * and a WebP encoder already, and it is the machine that had the bytes in the first place. The
 * gateway's job is to REFUSE what is too big — which it does — not to fix it.
 */

/** The widest a background is stored at. A 4K screen shows it upscaled, which for a photograph or a
 *  texture behind tiles is indistinguishable from the original and a quarter of the bytes. */
export const MAX_DIMENSION = 1920;

/** WebP quality. Chosen high enough that a gradient does not band — the failure mode that IS visible
 *  behind a dashboard — and low enough to stay in the low hundreds of kilobytes. */
export const WEBP_QUALITY = 0.82;

/** The gateway's own cap, mirrored so a person learns the picture is too big here rather than after
 *  a round trip. See `maxBackgroundBytes` in the api-gateway's dashboards.go. */
export const MAX_BACKGROUND_BYTES = 1024 * 1024;

/**
 * The size to draw at: the original, unless it is wider or taller than `max`.
 *
 * NEVER UPSCALES. A 600px image asked to fill a 1920 box would be stored four times larger and
 * exactly as blurry — more bytes for less picture, which is the wrong direction on both axes.
 */
export function fitWithin(width: number, height: number, max = MAX_DIMENSION): { width: number; height: number } {
  if (!(width > 0) || !(height > 0)) return { width: 0, height: 0 };
  const scale = Math.min(1, max / Math.max(width, height));
  // Rounded, and floored at 1: a very wide, very short image scaled by ratio can otherwise produce
  // a zero-height canvas, which throws rather than degrading.
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

/**
 * What kind of image these bytes are, read from the bytes.
 *
 * The blob store hands back CONTENT and not a type — the sha names the bytes, not their label — so
 * a caller that has fetched an image by address does not know what it is. `createImageBitmap`
 * sniffs the content and would decode it regardless, which is exactly why guessing was tempting and
 * exactly why it is wrong: a `Blob` labelled `image/png` holding a JPEG is a lie in the code that
 * the next reader will believe, and the first thing that DOES respect the label breaks quietly.
 *
 * Four magic numbers, and '' when none matches — which lets the caller say "that is not an image"
 * rather than fail later with something about a canvas.
 */
export function sniffImageType(bytes: Uint8Array): string {
  const b = bytes;
  if (b.length >= 8 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) return 'image/png';
  if (b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'image/jpeg';
  // RIFF....WEBP — the container tag is at byte 8, so the four bytes at 0 alone are not enough.
  if (b.length >= 12 && b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46 && b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50)
    return 'image/webp';
  // ISO-BMFF `ftyp` box at byte 4, brand `avif` at byte 8.
  if (b.length >= 12 && b[4] === 0x66 && b[5] === 0x74 && b[6] === 0x79 && b[7] === 0x70 && b[8] === 0x61 && b[9] === 0x76 && b[10] === 0x69 && b[11] === 0x66)
    return 'image/avif';
  return '';
}

export interface EncodedBackground {
  bytes: Uint8Array;
  mediaType: string;
  width: number;
  height: number;
}

/**
 * Decode, bound, and re-encode to WebP.
 *
 * Throws with a sentence rather than returning null: every caller turns this into a tool failure or
 * a toast, and a null would have to be given a message somewhere else — which is where the reason
 * for the failure gets lost.
 */
export async function encodeBackground(bytes: Uint8Array, mediaType: string): Promise<EncodedBackground> {
  // Sliced: a view into a larger buffer would blob the WHOLE buffer.
  const blob = new Blob([bytes.slice().buffer as ArrayBuffer], { type: mediaType });
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(blob);
  } catch {
    throw new Error(`Those bytes are not an image this browser can read (${mediaType}).`);
  }
  const { width, height } = fitWithin(bitmap.width, bitmap.height);
  const canvas = new OffscreenCanvas(width, height);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser could not open a canvas to resize the image.');
  ctx.drawImage(bitmap, 0, 0, width, height);
  // Released explicitly: an ImageBitmap holds decoded pixels — several megabytes for a large
  // image — until it is closed or collected, and this runs on a tab that is also driving a board.
  bitmap.close();

  const out = await canvas.convertToBlob({ type: 'image/webp', quality: WEBP_QUALITY });
  /*
   * A BROWSER THAT CANNOT ENCODE WEBP silently hands back a PNG rather than failing, and a PNG at
   * 1920 wide can still be over the cap. Checked rather than assumed, because the alternative is a
   * save refused by the gateway with a message about a limit the person did not choose.
   */
  const encoded = new Uint8Array(await out.arrayBuffer());
  if (encoded.byteLength > MAX_BACKGROUND_BYTES) {
    throw new Error(
      `That image is still ${Math.round(encoded.byteLength / 1024)} KB after compression, and a background is limited to ` +
        `${MAX_BACKGROUND_BYTES / 1024} KB. Try a simpler picture — a texture or a gradient compresses far better than a photograph.`,
    );
  }
  return { bytes: encoded, mediaType: out.type || 'image/webp', width, height };
}
